import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SuccessStories: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Platform News • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Success Stories: Indian Creators on IndeeClips
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Behind every subscriber count is a real person with a story. These Indian creators took a chance on IndeeClips when it was unknown, built audiences from zero, and turned content creation into careers. Their journeys prove that you don't need millions of followers to succeed—you need the right platform, consistency, and authentic connection with your audience.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Priya Sharma — Cooking in Bhojpuri (125K Subscribers)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Story
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Priya runs a small tiffin service in Patna, Bihar. She started filming cooking videos in Bhojpuri on her smartphone to attract local customers—never expecting an audience beyond her neighborhood. Within 6 months on IndeeClips, she had 50K subscribers. Today, she earns more from content than from her tiffin business.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          What Worked
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Regional language advantage:</strong> Bhojpuri cooking content was underserved—Priya filled a gap YouTube creators ignored</li>
          <li><strong>Authenticity:</strong> No fancy equipment, no scripted content—just Priya cooking in her home kitchen</li>
          <li><strong>Consistency:</strong> Uploaded 3 videos per week without fail</li>
          <li><strong>Community engagement:</strong> Responded to every comment in the first 3 months, building loyal audience</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Revenue Breakdown (Monthly)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Platform ad revenue: ₹35,000</li>
          <li>Local sponsorships (spice brands, kitchenware): ₹25,000</li>
          <li>Recipe e-book sales: ₹15,000</li>
          <li><strong>Total: ₹75,000/month</strong> (more than her tiffin business ever made)</li>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85, fontStyle: 'italic' }}>
          "YouTube पर मुझे कोई नहीं देखता था। IndeeClips ने Bhojpuri creators को मौका दिया।" — Priya
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Arjun Reddy — Tech Tutorials in Telugu (85K Subscribers)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Story
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Arjun worked IT support in Hyderabad, helping non-tech-savvy relatives troubleshoot phones and laptops. He realized millions of Telugu speakers struggled with technology but had no resources in their language. His first video—"How to use WhatsApp in Telugu"—got 10K views in 2 days. He quit his job 8 months later.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          What Worked
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Solving real problems:</strong> Topics like "How to block spam calls" and "How to install apps" resonate with non-tech audiences</li>
          <li><strong>Clear, patient teaching:</strong> Slow-paced tutorials assuming zero prior knowledge</li>
          <li><strong>Telugu-first content:</strong> Massive underserved audience in Andhra Pradesh and Telangana</li>
          <li><strong>SEO optimization:</strong> Titles like "WhatsApp లో గ్రూప్ ఎలా క్రియేట్ చేయాలి" rank in Telugu search</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Revenue Breakdown (Monthly)</Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Platform ad revenue: ₹40,000</li>
          <li>Affiliate marketing (phone cases, tech products): ₹30,000</li>
          <li>Paid 1-on-1 tech support sessions: ₹20,000</li>
          <li><strong>Total: ₹90,000/month</strong> (3x his IT support salary)</li>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85, fontStyle: 'italic' }}>
          "English tech tutorials exist everywhere. Telugu tutorials? IndeeClips gave me the audience YouTube never did." — Arjun
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Neha Gupta — Personal Finance (200K Subscribers)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Story
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Neha, a chartered accountant in Mumbai, noticed her friends making terrible financial decisions—no emergency fund, credit card debt, zero investment knowledge. She started making Hindi finance videos targeting middle-class Indians earning ₹30K-₹80K/month. Her practical advice (emergency funds before investing, index funds over stock picking) built a devoted following.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          What Worked
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>India-specific advice:</strong> PPF, ELSS, NPS—topics relevant to Indian tax and investment landscape</li>
          <li><strong>No jargon:</strong> Explained complex concepts in simple Hindi (e.g., "Mutual Fund SIP क्या है?")</li>
          <li><strong>Trustworthiness:</strong> Never promoted risky schemes, disclosed affiliate relationships</li>
          <li><strong>Consistent posting:</strong> Every Monday, 10 AM—audience knew when to expect new content</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Revenue Breakdown (Monthly)</Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Platform ad revenue: ₹80,000</li>
          <li>Sponsored videos (financial apps, banks): ₹1,20,000</li>
          <li>Paid financial planning consultations: ₹60,000</li>
          <li>Digital course sales ("Personal Finance Masterclass in Hindi"): ₹1,00,000</li>
          <li><strong>Total: ₹3,60,000/month</strong> (more than her CA practice)</li>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85, fontStyle: 'italic' }}>
          "IndeeClips' Hindi-first audience matched my target demographic perfectly. On YouTube, I'd be competing with English finance influencers. Here, I'm THE finance creator for Hindi speakers." — Neha
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Ravi Kumar — Village Vlogs (60K Subscribers)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Story
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Ravi lives in a village near Lucknow, Uttar Pradesh. He films daily life—farming, festivals, village gossip, cooking on wood stoves. No fancy editing, no clickbait—just raw, authentic rural India. His audience? Urban Indians nostalgic for village life and foreigners fascinated by rural India.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          What Worked
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Unique perspective:</strong> Most Indian creators are urban—Ravi's rural angle stands out</li>
          <li><strong>No competition:</strong> Very few village vloggers on IndeeClips when he started</li>
          <li><strong>Emotional connection:</strong> Viewers feel connected to a slower, simpler lifestyle</li>
          <li><strong>Daily uploads:</strong> Short 5-10 minute vlogs uploaded every single day</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Revenue Breakdown (Monthly)</Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Platform ad revenue: ₹25,000</li>
          <li>Selling organic produce to viewers: ₹15,000</li>
          <li>Brand deals (rural tourism, agricultural products): ₹20,000</li>
          <li><strong>Total: ₹60,000/month</strong> (transformed his family's income)</li>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85, fontStyle: 'italic' }}>
          "मैंने कभी सोचा नहीं था कि गांव की ज़िंदगी दिखाकर पैसे कमा सकता हूँ। IndeeClips ने मुझे मौका दिया।" — Ravi
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Common Patterns Across Success Stories
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          What do these creators have in common?
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Regional language content:</strong> Every success story leveraged non-English audiences</li>
          <li><strong>Underserved niches:</strong> They found gaps—Bhojpuri cooking, Telugu tech, Hindi finance, village vlogs</li>
          <li><strong>Consistency:</strong> All uploaded regularly (3x/week minimum)</li>
          <li><strong>Authenticity:</strong> No fake personas—viewers connected with real people</li>
          <li><strong>Early adoption:</strong> They joined IndeeClips before it was saturated</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Can You Replicate Their Success?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Yes—but not by copying their content. Instead:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Find YOUR niche:</strong> What do you know that others don't? What language/region/topic is underserved?</li>
          <li><strong>Start now:</strong> IndeeClips is still growing—early creators have the advantage</li>
          <li><strong>Commit to consistency:</strong> 1 video/week minimum, for at least 6 months</li>
          <li><strong>Engage with viewers:</strong> Reply to comments, ask for feedback, build community</li>
          <li><strong>Don't chase viral:</strong> Build a loyal 10K audience that trusts you {'>'} chasing 1M random views</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Your Success Story Starts Today
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            These creators weren't famous, rich, or tech-savvy when they started. They were ordinary Indians with something to share. If they can build audiences and quit their jobs, so can you. The question isn't whether IndeeClips can work for you—it's whether you're willing to put in the work.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Ready to start your creator journey? Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            {' '}for tips on getting started.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SuccessStories;
